import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Header } from './components/Header';
import { GanttChart } from './components/GanttChart';
import { TooltipPopup } from './components/TooltipPopup';
import { mockGanttService } from './services/ganttService';
import { GanttData, SortOption, Filter, TooltipData, Loom } from './types';
import { addDays } from 'date-fns';
import { useGoogleAnalytics, trackEvent } from './hooks/useGoogleAnalytics';

const App: React.FC = () => {
    // Inizializza Google Analytics
    useGoogleAnalytics();

    const [data, setData] = useState<GanttData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sort, setSort] = useState<SortOption>('Tel');
    const [filter, setFilter] = useState<Filter>({ type: 'art', value: '', add: '.StL' });
    const [dateRange] = useState({
        start: addDays(new Date(), -60),
        end: addDays(new Date(), 80),
    });
    const [tooltip, setTooltip] = useState<TooltipData>({ visible: false, content: null, x: 0, y: 0 });
    const [isTodayCentered, setIsTodayCentered] = useState(false);
    const ganttScrollRef = React.useRef<HTMLDivElement>(null);

    const getTodayScrollLeft = useCallback(() => {
        const el = ganttScrollRef.current;
        if (!el) return 0;
        const totalDuration = dateRange.end.getTime() - dateRange.start.getTime();
        const nowOffset = new Date().getTime() - dateRange.start.getTime();
        return (nowOffset / totalDuration) * el.scrollWidth - el.clientWidth / 2;
    }, [dateRange]);

    const handleSetToday = useCallback(() => {
        if (ganttScrollRef.current) {
            ganttScrollRef.current.scrollLeft = getTodayScrollLeft();
            setIsTodayCentered(true);
        }
    }, [getTodayScrollLeft]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const result = await mockGanttService.fetchAllData();
                setData(result);
                setError(null);
            } catch (err) {
                setError('Impossibile caricare i dati della programmazione.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        // Scroll to today's date when the chart is first loaded.
        if (!isLoading && data) {
            // Use a timeout to allow the browser to render and calculate layout.
            const timer = setTimeout(() => {
                handleSetToday();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isLoading, data, handleSetToday]);

    useEffect(() => {
        if (isLoading || !data) return;
        const el = ganttScrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const target = getTodayScrollLeft();
            setIsTodayCentered(Math.abs(el.scrollLeft - target) <= 5);
        };
        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [isLoading, data, getTodayScrollLeft]);

    const handleFilterChange = useCallback((newFilter: Partial<Filter>) => {
        setFilter(prev => ({ ...prev, ...newFilter }));
        trackEvent('Gantt', 'Filter Change', newFilter.type || 'unknown');
    }, []);

    const handleSortChange = useCallback((newSort: SortOption) => {
        setSort(newSort);
        trackEvent('Gantt', 'Sort Change', newSort);
    }, []);
    
    const sortLooms = useCallback((looms: Loom[]) => {
        return looms.sort((a, b) => {
            switch(sort) {
                case 'Start': return a.start.getTime() - b.start.getTime();
                case 'Stop': return a.end.getTime() - b.end.getTime();
                case 'Tel': default: return a.name.localeCompare(b.name);
            }
        });
    }, [sort]);

    const filterMatches = useMemo<{ looms: Set<string>; beams: Set<string>; pieces: Set<string> } | null>(() => {
        if (!data) return null;
        if (filter.value.trim() === '' && !filter.add) return null;

        const filterValueUpper = filter.value.trim().toUpperCase();
        const looms = new Set<string>();
        const beams = new Set<string>();
        const pieces = new Set<string>();

        Object.values(data.looms).forEach(loom => {
            const loomWarpBeams = data.warpBeamsByLoom[loom.id] || [];
            for (const warpBeam of loomWarpBeams) {
                let beamMatch = false;
                const warpBeamClothPieces = data.clothPiecesByWarpBeam[warpBeam.id] || [];

                if (filter.type === 'art') {
                    for (const piece of warpBeamClothPieces) {
                        if (filterValueUpper === '' || piece.article.toUpperCase().includes(filterValueUpper)) {
                            if (!filter.add || filter.add === piece.displayStatus.statusClass) {
                                pieces.add(piece.id);
                                beamMatch = true;
                            }
                        }
                    }
                } else if (filter.type === 'ord') {
                    if (filterValueUpper !== '' && warpBeam.ordito.toUpperCase().includes(filterValueUpper)) {
                        beamMatch = true;
                        warpBeamClothPieces.forEach(p => pieces.add(p.id));
                    }
                } else if (filter.type === 'ann') {
                    for (const piece of warpBeamClothPieces) {
                        if (filterValueUpper === '' || piece.annodatura.toUpperCase().includes(filterValueUpper)) {
                            if (!filter.add || filter.add === piece.displayStatus.statusClass) {
                                pieces.add(piece.id);
                                beamMatch = true;
                            }
                        }
                    }
                } else if (filter.type === 'part') {
                    if (filterValueUpper !== '' && warpBeam.sb.toUpperCase().includes(filterValueUpper)) {
                        beamMatch = true;
                        warpBeamClothPieces.forEach(p => pieces.add(p.id));
                    }
                }

                if (beamMatch) {
                    beams.add(warpBeam.id);
                    looms.add(loom.id);
                }
            }
        });
        return { looms, beams, pieces };
    }, [data, filter]);

    const matchingLoomIds = filterMatches ? filterMatches.looms : null;
    const matchingBeamIds = filterMatches ? filterMatches.beams : null;
    const matchingPieceIds = filterMatches ? filterMatches.pieces : null;

    const filteredAndSortedLooms = useMemo<Loom[]>(() => {
        if (!data) return [];
        const allLooms = Object.values(data.looms);
        const filtered = matchingLoomIds ? allLooms.filter(l => matchingLoomIds.has(l.id)) : allLooms;
        return sortLooms([...filtered]);
    }, [data, matchingLoomIds, sortLooms]);

    const allSortedLooms = useMemo<Loom[]>(() => {
        if (!data) return [];
        return sortLooms([...Object.values(data.looms)]);
    }, [data, sortLooms]);


    const showTooltip = useCallback((content: React.ReactNode, pos: { x: number; y: number }) => {
        setTooltip({ visible: true, content, x: pos.x, y: pos.y });
    }, []);

    const hideTooltip = useCallback(() => {
        setTooltip(prev => ({ ...prev, visible: false }));
    }, []);

    // Su tocco il tooltip si apre con un tap: un tap fuori dagli elementi con tooltip lo chiude
    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            if (!(e.target as HTMLElement).closest('[data-tip]')) hideTooltip();
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [hideTooltip]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-xl font-semibold" role="status">Caricamento programma telai...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                <div className="text-xl font-semibold">{error}</div>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <div className="weaving-gantt-container" style={{minHeight: '100vh'}}>
            <Header
                sort={sort}
                onSortChange={handleSortChange}
                filter={filter}
                onFilterChange={handleFilterChange}
                onSetToday={handleSetToday}
                isTodayCentered={isTodayCentered}
                data={data}
                filteredLooms={filteredAndSortedLooms}
            />
            <main className="flex-1 min-h-0 relative mt-2 mb-2 mx-1">
                <div className="absolute inset-0">
                <GanttChart
                    ref={ganttScrollRef}
                    data={data}
                    looms={allSortedLooms}
                    matchingLoomIds={matchingLoomIds}
                    matchingBeamIds={matchingBeamIds}
                    matchingPieceIds={matchingPieceIds}
                    dateRange={dateRange}
                    showTooltip={showTooltip}
                    hideTooltip={hideTooltip}
                />
                </div>
            </main>
            <TooltipPopup tooltip={tooltip} />
        </div>
    );
};

export default App;
